import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
} from "@mui/material";
import {
  LockOpen as UnfreezeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import DoApiService from "../../../services/DoApiService";

const CollectionCenter = () => {
  const navigate = useNavigate();

  // State management
  const [username, setUsername] = useState("");
  const [collectionCenter, setCollectionCenter] = useState("");
  const [sampleFile, setSampleFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [appId, setAppId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });


const handleUsernameSearch = async () => {
  try {
    const userData = DoApiService.getUserData();
    const response = await DoApiService.validateUsername(username, userData.orgId);

    if (response.success) {
      setAppId(response.data.appId);
      setShowForm(true);
    } else {
      toast.error(
        "Record does not exist. Kindly recheck your LAB ID/Login Name."
      );
    }
  } catch (error) {
    toast.error(error.message);
  }
};

const handleCollectionCenterChange = (e) => {
  const value = e.target.value.replace(/\D/g, "");

  setCollectionCenter(value);

  if (parseInt(value || 0) > 0) {
    setShowUpload(true);
  } else {
    setShowUpload(false);
    setSampleFile(null);
  }
};
  // Handle form submission
const handleSubmit = async () => {
  try {
    if (!collectionCenter) {
      toast.error("Please enter collection center");
      return;
    }

    if (
      parseInt(collectionCenter) > 0 &&
      !sampleFile
    ) {
      toast.error("Please select file");
      return;
    }

    const formData = new FormData();

    formData.append("appId", appId);
    formData.append(
      "collectionCenter",
      collectionCenter
    );

    if (sampleFile) {
      formData.append("file", sampleFile);
    }

    const response = await DoApiService.saveCollectionCenter(formData);

    if (response.success) {
      toast.success("Collection Center updated successfully");
      setUsername("");
      setCollectionCenter("");
      setSampleFile(null);
      setShowForm(false);
      setShowUpload(false);
      setAppId(null);
    } else {
      toast.error(response.message || "Failed to update Collection Center");
    }
  } catch (error) {
    toast.error(error.message);
  }
};


  // Show snackbar
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

 

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Collection Center...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          pt: 2,
          pb: 4,
        }}
      >
        {/* Header with Gradient Background */}
        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
            color: "white",
            borderRadius: 0,
            mb: 3,
          }}
        >
          <Box sx={{ px: 3, py: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
              sx={{
                mb: 2,
                "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
              }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleBackClick}
                sx={{
                  textDecoration: "none",
                  color: "rgba(255,255,255,0.8)",
                  "&:hover": { color: "white" },
                }}
              >
                Dashboard
              </Link>
              <Typography variant="body2" sx={{ color: "white" }}>
                Collection Center
              </Typography>
            </Breadcrumbs>

            {/* Header Content */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    mr: 2,
                    width: 56,
                    height: 56,
                  }}
                >
                  <UnfreezeIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Collection Center
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackClick}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            label="Lab ID / Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button variant="contained" onClick={handleUsernameSearch}>
            Search
          </Button>

          {showForm && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Collection Center"
                value={collectionCenter}
                onChange={handleCollectionCenterChange}
                sx={{ mb: 2 }}
              />

              {parseInt(collectionCenter || 0) > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    component="a"
                    href="/assets/SCF%20format.xls"
                    target="_blank"
                    sx={{ mr: 2 }}
                  >
                    Download Format
                  </Button>

                  <input
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={(e) => setSampleFile(e.target.files[0])}
                  />
                </Box>
              )}

              <Button variant="contained" onClick={handleSubmit}>
                Submit
              </Button>
            </Box>
          )}
        </Box>
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default CollectionCenter;